// Anonymous session id for ratesearch activity attribution (activity-crm epic
// PR-2, decision D2). A stable per-browser uuid, persisted in localStorage, so
// a visitor's pre-unlock searches can be stitched to their Lead the moment they
// unlock (pf-comms stitchSession). Survives reloads; one visitor = one id until
// they clear storage. No PII — it's an opaque uuid, never an email/name.

const STORAGE_KEY = 'pf_rs_session_uid';

// A v4 uuid from the Web Crypto API. crypto.randomUUID covers every browser
// this app targets; the fallback still uses crypto (getRandomValues), never
// Math.random — the id is opaque but a CSPRNG keeps it unguessable and avoids a
// weak-RNG footgun. Returns null if no crypto is available (caller degrades).
const mintUuid = () => {
  const c = typeof crypto !== 'undefined' ? crypto : null;
  if (!c) return null;
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  if (typeof c.getRandomValues !== 'function') return null;
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
};

/**
 * The current browser's anonymous session id, minting + persisting one on first
 * call. Returns null if localStorage is unavailable (private-mode lockdowns) or
 * crypto can't mint one — callers treat null as "no anon identity", never an
 * error.
 * @returns {string|null}
 */
export const getSessionUid = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const uid = mintUuid();
    if (uid) window.localStorage.setItem(STORAGE_KEY, uid);
    return uid;
  } catch {
    return null; // storage blocked — degrade to no anon identity
  }
};

/** BB `source` enum values (must match the Ratesearchactivity list property). */
export const ActivitySource = Object.freeze({
  RATESEARCH_WEB: 'RATESEARCH_WEB',
  LANDING_PAGE: 'LANDING_PAGE',
  PORTAL_IFRAME: 'PORTAL_IFRAME',
});

/**
 * Which surface this search/event came from, for analytics segmentation.
 * Embedded in the portal (or any parent) => PORTAL_IFRAME; the standalone
 * landing page => LANDING_PAGE; otherwise the public ratesearch site.
 * @returns {string} an ActivitySource value
 */
export const getActivitySource = () => {
  try {
    if (typeof window === 'undefined') return ActivitySource.RATESEARCH_WEB;
    if (window.self !== window.top) return ActivitySource.PORTAL_IFRAME;
    if (window.location?.pathname?.includes('/landing-page')) {
      return ActivitySource.LANDING_PAGE;
    }
  } catch {
    // cross-origin frame access throws → we are framed
    return ActivitySource.PORTAL_IFRAME;
  }
  return ActivitySource.RATESEARCH_WEB;
};

/**
 * Attribution context for a rate-search request's `activity` block. Always
 * carries `source`; includes `sessionUid` only when storage yielded one (never
 * a `sessionUid: null` key that could trip upstream optional-field validation).
 * @returns {{ source: string, sessionUid?: string }}
 */
export const getActivityContext = () => {
  const ctx = { source: getActivitySource() };
  const uid = getSessionUid();
  if (uid) ctx.sessionUid = uid;
  return ctx;
};
